import * as React from 'react';

import {
    type HiveBranchPresence,
    HiveDataSyncState,
    type HiveGlobalPresence,
    type HiveWorkingBranch,
} from '@gitbook/hive-client';
import { CONTENT_DEFAULT_BRANCH } from '@gitbook/hive-core';

import { HiveGlobalPresenceContext } from './HiveContext';
import { useHiveClient } from './useHiveClient';
import { HiveRevisionProvider } from './useHiveRevision';

const HiveBranchContext = React.createContext<HiveWorkingBranch | null>(null);

type BranchPresenceSpecific = Omit<HiveBranchPresence, keyof HiveGlobalPresence>;

interface UseHiveBranchOptions {
    contentId: string;
    branchId: string | undefined;
    presence?: BranchPresenceSpecific;
    readOnly?: boolean;
}

/**
 * Provide a specific content branch to the react tree.
 */
export function HiveBranchProvider(
    props: React.PropsWithChildren<UseHiveBranchOptions>
): React.ReactElement {
    const { contentId, branchId, presence, readOnly, children } = props;

    const branch = useHiveBranchById({ contentId, branchId, presence, readOnly });

    return (
        <HiveBranchContext.Provider value={branch}>
            <HiveRevisionProvider branch={branch}>{children}</HiveRevisionProvider>
        </HiveBranchContext.Provider>
    );
}

/**
 * Return the current hive branch. Does not rerender when the HiveWorkingBranch changes.
 * If you need to rerender on change, consider useHiveWorkingBranch for revisions or
 * useHiveDataSyncClock for clocks.
 */
export function useHiveBranch(): HiveWorkingBranch | null {
    const current = React.useContext(HiveBranchContext);
    return current;
}

/**
 * Get a branch by its ID in a content.
 */
export function useHiveBranchById(props: UseHiveBranchOptions): HiveWorkingBranch {
    const { contentId, branchId, presence = {}, readOnly = false } = props;

    const client = useHiveClient();

    const branch = client.sync.branch(contentId, branchId || CONTENT_DEFAULT_BRANCH, {
        readOnly,
    });
    const promise = branch.open();
    if (promise) {
        throw promise;
    }

    useHiveBranchSetPresence({
        branch,
        presence,
    });

    React.useEffect(() => {
        const keepAlive = branch.keepAlive();
        return () => {
            // Clear the presence when the branch is changed
            // Because the data sync stays active in the background, but we don't want to continue showing the user as active.
            if (branch.dataSync.state === HiveDataSyncState.OPEN) {
                branch.dataSync.setPresence({ data: {} });
            }
            keepAlive();
        };
    }, [branch]);

    return branch;
}

/**
 * Update the presence on the current branch.
 */
export function useHiveBranchSetPresence(props: {
    branch?: HiveWorkingBranch;
    presence: BranchPresenceSpecific;
}) {
    const defaultBranch = useHiveBranch();
    const { branch = defaultBranch, presence } = props;

    const globalPresence = React.useContext(HiveGlobalPresenceContext);

    React.useEffect(() => {
        if (!globalPresence && process.env.NODE_ENV === 'development') {
            throw new Error(
                'No global presence has been set on the HiveProvider before creating data syncs.'
            );
        }

        if (branch && globalPresence) {
            branch.dataSync.setPresence({
                data: {
                    ...branch.dataSync.presence?.data,
                    ...globalPresence,
                    pageId: presence?.pageId,
                },
            });
        }
    }, [globalPresence, presence?.pageId]);
}

/**
 * Preload a branch by its ID in a content.
 */
export function useHivePreloadBranch(props: UseHiveBranchOptions | null) {
    const client = useHiveClient();

    if (!props) {
        return;
    }

    const branch = client.sync.branch(props.contentId, props.branchId || CONTENT_DEFAULT_BRANCH, {
        readOnly: props.readOnly,
    });
    if (branch.dataSync.state === HiveDataSyncState.CLOSED) {
        // Only open the branch if it's not already open
        // to avoid updating the presence.
        //
        // and do not suspend on preload
        void branch.open();
    }
}
